import { memo, useMemo, useState } from 'react';
import { SectionRenderer } from '@/components/sections/SectionRenderer';
import { DecorationNote } from '@/components/sections/DecorationSections';
import { attachExplanations } from '@/components/sections/ProductRecommendations';
import { BlogSuggestions } from '@/components/BlogSuggestions';
import { ChatContextProvider, useChatContext } from './ChatContext';
import { FeedbackButtons } from './FeedbackButtons';
import { WelcomeBubble } from './WelcomeBubble';
import { OverviewEchoBubble } from './OverviewEchoBubble';
import { FailedBubble } from './FailedBubble';
import { Dots, TypingIndicator } from './TypingIndicator';
import { cutFlow, flowLength, useTypewriter } from './typewriter';
import { mergeProviderPayloads } from '@/utils/providerPayload';
import type { ChatMessage, StreamBlock } from './messages';
import type { ProviderPayload, RenderableResponse, Section } from '@/types/chat';

export interface MessageBubbleProps {
  message: ChatMessage;
}

export function MessageBubble({ message }: MessageBubbleProps): JSX.Element {
  switch (message.type) {
    case 'welcome':
      return <WelcomeBubble />;
    case 'echo':
      return <OverviewEchoBubble context={message.context} />;
    case 'user':
      return (
        <div className="tw-flex tw-justify-end">
          <div className="tw-max-w-[80%] tw-bg-cal-green-50 tw-text-cal-ink tw-rounded-2xl tw-rounded-tr-md tw-px-4 tw-py-2.5 tw-text-[14px] tw-leading-relaxed tw-whitespace-pre-wrap tw-shadow-cal-sm">
            {message.text}
          </div>
        </div>
      );
    // The same bubble while the reply streams and once it is done (same key, same component):
    // it carries on typing where it was, and the final reply only adds what the stream had not.
    case 'assistant':
      return <ReplyBubble message={message} />;
    case 'streaming':
      return message.blocks.length > 0 ? <ReplyBubble message={message} /> : <TypingIndicator stage={message.stage} />;
    case 'failed':
      return <FailedBubble message={message} />;
  }
}

/**
 * The notes around the answer — the intro, the red flags, the precaution reminder,
 * the AI notice: read last, after the answer and its blog suggestions, in the
 * backend's order.
 */
const DISCLAIMER_TYPES = ['skill_intro', 'red_flags', 'product_precaution', 'ai_notice'] as const;
type Disclaimer = Extract<Section, { type: (typeof DISCLAIMER_TYPES)[number] }>;
const isDisclaimer = (s: Section | null | undefined): s is Disclaimer =>
  !!s && (DISCLAIMER_TYPES as readonly string[]).includes(s.type);

/** The stream so far as the reply's own sections: its text blocks and card groups, in order. */
function streamSections(blocks: StreamBlock[]): { sections: Section[]; payload: ProviderPayload } {
  return {
    sections: blocks.flatMap((b): Section[] => {
      if (b.kind === 'cards') return [{ type: 'product_recommendations', skus: b.skus }];
      const body_md = b.md.trim(); // as the final reply has it
      return body_md ? [{ type: 'direct_answer', title: null, body_md }] : [];
    }),
    payload: { products: blocks.flatMap((b) => (b.kind === 'cards' ? b.products : [])), blogs: [], pages: [] },
  };
}

function ReplyBubble({ message }: { message: Extract<ChatMessage, { type: 'assistant' | 'streaming' }> }): JSX.Element {
  const parent = useChatContext();
  const final = message.type === 'assistant' ? message.response : null;

  const { sections, payload } = useMemo(() => {
    if (message.type === 'streaming') return streamSections(message.blocks);
    const { structured_reply: sr, reply, provider_payload } = message.response;
    return {
      // no structured reply: its markdown fallback, so links, lists and bold still render
      sections: sr && Array.isArray(sr.sections) ? sr.sections : [{ type: 'direct_answer', title: null, body_md: reply } as Section],
      payload: provider_payload,
    };
  }, [message]);
  const flow = useMemo(() => attachExplanations(sections.filter((s) => !isDisclaimer(s))), [sections]);
  const notes = useMemo(() => sections.filter(isDisclaimer), [sections]);
  // Prefer this message's fresh objects, then fall back to items returned in
  // earlier turns of the same session.
  const merged = useMemo(() => mergeProviderPayloads(payload, parent.payload), [payload, parent.payload]);

  // Typed out before the reader when it comes live (streaming, or arrived during this visit);
  // shown whole when it comes from history. Decided once: the stream's bubble keeps it after done.
  const [live] = useState(() => message.type === 'streaming' || !!message.reveal);

  return (
    <ChatContextProvider {...parent} payload={merged}>
      <TypedReply flow={flow} notes={notes} final={final} live={live} />
    </ChatContextProvider>
  );
}

const MemoSection = memo(SectionRenderer);

/**
 * The bubble, typing its flow out. On its own below the context, so that it alone re-renders
 * from frame to frame — and in it only the section being typed (the others are memoized).
 */
function TypedReply({
  flow,
  notes,
  final,
  live,
}: {
  flow: Section[];
  notes: Disclaimer[];
  final: RenderableResponse | null;
  live: boolean;
}): JSX.Element {
  const total = flowLength(flow);
  const shown = useTypewriter(total, live);
  const typed = shown >= total;
  // the rest (blog suggestions, notes, feedback) once the final reply is in and typed out
  const complete = final !== null && typed;
  const blogs = final?.provider_payload.blogs ?? [];

  // Assistant bubble = flat white (no border, no shadow), against the user
  // bubble's green tint. Redline (emergency) variant uses the red palette
  // instead. Both share the same rounded geometry.
  const wrapperClass = final?.is_redline_short_circuit ? 'tw-bg-red-50 tw-border-2 tw-border-red-300' : 'tw-bg-cal-surface';

  return (
    <div
      aria-busy={!complete}
      className={`cal-assistant-reply${live ? ' is-live' : ''} tw-rounded-2xl tw-rounded-tl-md tw-p-4 tw-flex tw-flex-col tw-gap-3 ${wrapperClass}`}
    >
      {(typed ? flow : cutFlow(flow, shown)).map((section, i) => (
        <MemoSection key={i} section={section} />
      ))}
      {!final && (
        <div className="tw-text-xs tw-text-cal-green-700 tw-italic tw-flex tw-items-center tw-gap-2 tw-px-3">
          <Dots />
          <span>en cours…</span>
        </div>
      )}
      {complete && blogs.length > 0 && <BlogSuggestions blogs={blogs} />}
      {complete && notes.length > 0 && (
        <div className="cal-disclaimers">
          {notes.map((note, i) => (
            <DecorationNote key={i} body={note.body_md} />
          ))}
        </div>
      )}
      {complete && <FeedbackButtons messageBackendId={final.assistant_message.message_id} />}
    </div>
  );
}
